import React, { useLayoutEffect, useRef, useState } from 'react';
import { Button } from '@innovationnorway/cl-react';
import css from './ReadMore.module.scss';
import { useTranslation } from 'react-i18next';

const ReadMore = ({
  children,
  maxHeight = '6rem',
  buttonBeside = false,
}: {
  children: React.ReactNode;
  /** Collapsed height as a CSS value. */
  maxHeight?: string;
  /**
   * When true, the show more/less button is rendered beside the content instead of below it.
   * Useful in table rows to keep a consistent row height.
   */
  buttonBeside?: boolean;
}) => {
  const [expanded, setExpanded] = useState(false);
  const [needsReadMore, setNeedsReadMore] = useState(false);
  const [fullHeight, setFullHeight] = useState<number | undefined>(undefined);
  const contentRef = useRef<HTMLDivElement>(null);
  const { t: tCommon } = useTranslation('common');

  useLayoutEffect(() => {
    const element = contentRef.current;
    if (!element) return;

    const measure = () => {
      setFullHeight(element.scrollHeight);
      if (!expanded) {
        setNeedsReadMore(element.scrollHeight > element.clientHeight);
      }
    };

    measure();

    const resizeObserver = new ResizeObserver(() => {
      measure();
    });
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [maxHeight, expanded, children]);

  const isClipped = !expanded && needsReadMore;

  return (
    <div className={`${buttonBeside ? css['inline'] : ''}`}>
      <div
        ref={contentRef}
        className={`${css['content']} ${isClipped ? css['clipped'] : ''}`}
        style={
          {
            '--read-more-height': expanded && fullHeight !== undefined ? `${fullHeight}px` : maxHeight,
          } as React.CSSProperties
        }
      >
        {children}
      </div>
      {needsReadMore && (
        <Button variant="tertiary" type="button" onClick={() => setExpanded((prev) => !prev)}>
          {expanded ? tCommon('showLess') : tCommon('showMore')}
        </Button>
      )}
    </div>
  );
};

export default ReadMore;
